import React, { useCallback, useContext, useMemo } from 'react'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { OfferPageActions, OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'LuxKit/Button/TextButton'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import Group from 'components/utils/Group'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import Caption from 'LuxKit/Typography/Caption'
import GeoContext from 'contexts/geoContext'
import { FLIGHTS_MIN_LEAD_TIME_DAYS } from 'constants/flight'

interface Props {
  offer: App.Offer
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  isLoggedIn: boolean
  checkIn?: string
  currency: string
  buyNowBookLater: boolean
  room: OfferPageRoom
  hasFlights: boolean
  packageOption: App.OfferPackageOptionView
  isFetching?: boolean
  defaultDepositAmountPercentage: number
  showPoints: boolean
  flightPrice?: number
}

function MultipleRoomsBookingPriceDetails(props: Props) {
  const {
    offer,
    flightOrigin,
    flightDestination,
    checkIn,
    currency,
    buyNowBookLater,
    room,
    hasFlights,
    packageOption,
    isFetching,
    defaultDepositAmountPercentage,
    showPoints,
    flightPrice,
  } = props

  const {
    package: pkg,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelMemberPrice,
    baseMemberPrice,
    propertyFees,
  } = packageOption

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const offerPageDispatch = useOfferPageDispatch()
  const showIncludedGuestsExceeded = useMemo(() => hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])

  const departureTooEarly = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInMoment])

  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330
  }, [checkInMoment])

  const totalPrice = hotelPrice + (propertyFees ?? 0)
  const totalMemberPrice = useMemo(() => {
    if (hotelMemberPrice > 0) {
      return hotelMemberPrice + (propertyFees ?? 0)
    }
    return 0
  }, [hotelMemberPrice, propertyFees])
  const showMemberPrice = baseMemberPrice > 0

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && !offer.isDiscountPillHidden
  const shouldShowValue = hotelValue > (showMemberPrice ? hotelMemberPrice : hotelPrice) && pkg.shouldDisplayValue
  const shouldShowBNBL = packageOption.capacityValid && offer.hasBuyNowBookLater && pkg.allowBuyNowBookLater && buyNowBookLater
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, (showMemberPrice ? hotelMemberPrice : hotelPrice)) && !buyNowBookLater

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (!showPoints) { return [] }
    return [
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView: packageOption, currentRegionCode }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView: packageOption, flightPrice, skip: !hasFlights }),
    ]
  }, [showPoints, offer, packageOption, flightPrice, hasFlights, currentRegionCode])

  return (
    <Group direction="vertical" gap={12}>
      {shouldShowBNBL && (
        <StructureLabel startIcon={<LineCheckIcon size="XS" />} variant="light">Buy now, choose dates later</StructureLabel>
      )}
      {isDepositEnabled && <SecureWithDepositTagAndModal offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds} />}
      <Group direction="vertical" gap={12}>
        {!hasFlights && (
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4} data-testid="pricing">
              <PriceRowPriceCaption>{pluralizeToString('night', pkg.duration)} from</PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="S"
                price={totalPrice}
                memberPrice={totalMemberPrice}
                saleUnit="total"
                offerConfig={offer.luxPlus}
              />
              <PriceRowTaxesAndFees />
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={hotelValue}
                discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
              />}
              {showIncludedGuestsExceeded && (
                <AdditionalGuestsPopup
                  complex={false}
                  description="+ Extra guest surcharge"
                  modalContent={pkg.roomPolicyDescription}
                />
              )}
            </Group>
            <LoyaltyPointsEarnWithBreakdownModal
              calculationRequests={pointsEarnCalculationRequests}
              calculationType={(!checkIn || hasFlights) ? 'estimate' : 'exact'}
            />
          </Group>
        )}

        {hasFlights && (
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4} data-testid="flights-pricing">
              {(() => {
                if (flightOrigin?.code === flightDestination?.code) {
                  return <Group direction="vertical" gap={8}>
                    <PriceRowCaption>
                      No flights available from {flightOrigin?.name}. Please select another departing airport.
                    </PriceRowCaption>
                    <TextButton
                      kind="tertiary"
                      onClick={onToggleFilter}
                      data-filter="airport"
                    >
                      Change airport
                    </TextButton>
                  </Group>
                }
                else if (departureTooEarly) {
                  return <Group direction="vertical" gap={8}>
                    <PriceRowCaption>
                      Departure date is too soon.
                    </PriceRowCaption>
                  </Group>
                }
                else if (departureTooFarInFuture) {
                  return <Group direction="vertical" gap={8}>
                    <PriceRowCaption>
                      Flights can only be booked up to 11 months in advance.
                    </PriceRowCaption>
                  </Group>
                }
                else if (flightPrice) {
                  return <Group direction="vertical" gap={4}>
                    <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                      {pluralizeToString('night', pkg.duration)} + flights from
                    </PriceRowPriceCaption>
                    <LuxPlusPriceStack
                      size="S"
                      price={totalPrice + flightPrice}
                      memberPrice={totalMemberPrice > 0 ? totalMemberPrice + flightPrice : 0}
                      saleUnit="total*"
                      offerConfig={offer.luxPlus}
                    />
                    <PriceRowTaxesAndFees />
                    {shouldShowValue && <PriceRowValueDiscountWithCaption
                      data-testid="value-box"
                      size="M"
                      originalValue={hotelValue + flightPrice}
                    />}
                    {showIncludedGuestsExceeded && (
                      <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={pkg.roomPolicyDescription}
                      />
                    )}
                  </Group>
                }
                else {
                  return <Group direction="vertical" gap={4}>
                    <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                      {pluralizeToString('night', pkg.duration)} from
                    </PriceRowPriceCaption>
                    <LuxPlusPriceStack
                      size="S"
                      price={totalPrice}
                      memberPrice={totalMemberPrice > 0 ? totalMemberPrice : 0}
                      saleUnit=""
                      offerConfig={offer.luxPlus}
                    />
                    <Caption variant="large">+ flights</Caption>
                    {showIncludedGuestsExceeded && (
                      <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={pkg.roomPolicyDescription}
                      />
                    )}
                  </Group>
                }
              })()}
              <AtolProtectedTooltip />
            </Group>
            <LoyaltyPointsEarnWithBreakdownModal
              calculationRequests={pointsEarnCalculationRequests}
              calculationType={(!checkIn || hasFlights) ? 'estimate' : 'exact'}
            />
          </Group>
        )}
        <LoadingIndicator visible={isFetching} floating opaque />
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  currency: appState.geo.currentCurrency,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  isLoggedIn: isLoggedInSelector(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}), MultipleRoomsBookingPriceDetails)
